import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { useI18nContext } from '@/i18n/i18n-react';
import { Button } from '@/components/ui/button';
import { TableHead } from '@/components/ui/table';

export type SortDirection = 'ascending' | 'descending' | 'none';

/** Reads the direction of `field` out of a `<field>_desc` / `<field>_asc` sort. */
export function getSortDirection(
  field: string,
  current: string
): SortDirection {
  if (current === `${field}_desc`) return 'descending';
  if (current === `${field}_asc`) return 'ascending';
  return 'none';
}

/** First click sorts descending (newest / highest first); the next flips it. */
export function getNextSort<T extends string>(
  field: string,
  current: string,
  sorts: readonly T[]
): T | undefined {
  const target = current === `${field}_desc` ? `${field}_asc` : `${field}_desc`;
  return sorts.find((sort) => sort === target);
}

interface OrdersDrilldownSortHeaderProps {
  label: string;
  direction: SortDirection;
  onSort: () => void;
  alignRight?: boolean;
}

export function OrdersDrilldownSortHeader({
  label,
  direction,
  onSort,
  alignRight = false,
}: OrdersDrilldownSortHeaderProps) {
  const { LL } = useI18nContext();
  const Icon =
    direction === 'ascending'
      ? ArrowUp
      : direction === 'descending'
        ? ArrowDown
        : ArrowUpDown;

  return (
    <TableHead
      aria-sort={direction}
      className={alignRight ? 'text-right' : undefined}
    >
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={`-mx-2 h-8 gap-1 px-2 font-medium ${alignRight ? 'ml-auto flex' : ''}`}
        aria-label={LL.metricsPage.repurchaseDrilldown.sortByAriaLabel({
          column: label,
        })}
        onClick={onSort}
      >
        {label}
        <Icon
          className={`size-3.5 ${direction === 'none' ? 'opacity-40' : ''}`}
          aria-hidden="true"
        />
      </Button>
    </TableHead>
  );
}
